import { useState, useEffect, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import BankIcon from '@material-ui/icons/AccountBalance';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';

import Modal from 'components/CncUI/Modal';
import Table from 'components/CncUI/Table';
import Typography from 'components/CncUI/Typography';
import Link from 'components/CncUI/Link';
import Button from 'components/CncUI/Button';
import TextField from 'components/CncUI/TextField';
import Checkbox from 'components/CncUI/Checkbox';
import Radio from 'components/CncUI/Radio';

import Loader from 'components/Loader';
import DeleteModal from '../DeleteModal';
import LimitModal from '../LimitModal';
import BankStatus from './BankStatus';
import BankAction from './BankAction';
import PlaidButton from './PlaidButton';
import PlaidOAuthButton from './PlaidOAuthButton';

import useRemoveBank from '../hooks/useRemoveBank';
import useExpireBank from '../hooks/useExpireBank';
import useUpdateBank from '../hooks/useUpdateBank';
import useAddBank from '../hooks/useAddBank';
import useBanks from '../hooks/useBanks';
import useGetAccounts from '../hooks/useGetAccounts';
import useAddPlaidError from '../hooks/useAddPlaidError';
import useAuthentication from 'hooks/useAuthentication';
import useQuery from 'hooks/useQuery';
import useCountry from 'hooks/useCountry';

import getAPIServer from 'constants/config/apiServer';
import getStorage from 'utilities/getStorage';
import parseBank from './utils/bankName';
import Logger from 'utilities/logger';
import format from 'utilities/format';

const styles = ({ palette, breakpoints }) => ({
  empty: {
    '& .cncui-table-cell': {
      textAlign: 'center',
      height: '5em',
      fontStyle: 'italic',
      color: palette.cncUI.default.primary,
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  addButton: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    [breakpoints.only('xs')]: {
      justifyContent: 'flex-start',
    },
  },
  currencyOptions: {
    paddingLeft: 30,
    maxWidth: 300,
  },
  link: {
    color: palette.mycc.link,
  },
  addIcon: {
    marginRight: 4,
  },
  content: {
    paddingBottom: 0,
  },
  text: {
    fontSize: 14,
    color: palette.mycc.darkText,
    marginTop: 0,
  },
  smallColumn: {
    width: 44,
  },
  button: {
    marginTop: 8,
    marginBottom: 8,
    padding: '6px 24px',
    marginRight: 20,
  },
  tableHead: {
    backgroundColor: palette.mycc.gray,
    height: 38,
  },
  row: {
    cursor: 'pointer',
    backgroundColor: palette.mycc.lightGray,
    '& td': {
      fontSize: 16,
      color: palette.text.secondary,
    },
  },
  tableRow: {
    backgroundColor: palette.mycc.white,
    '& td': {
      fontSize: 16,
    },
  },
  bankIconCell: {
    paddingLeft: 16,
    paddingRight: 8,
    width: 40,
  },
  noInfo: {
    color: palette.mycc.disableLabel,
    fontSize: 16,
  },
  bankIcon: {
    width: 36,
    verticalAlign: 'middle',
  },
  subTitle: {
    marginBottom: 0,
  },
  remove: {
    color: palette.cncUI.alert.red.primary,
  },
  description: {
    color: palette.cncUI.primary.gray['+2'],
    margin: 0,
  },
});

const BankAccounts = props => {
  const { user } = useAuthentication();
  const { country } = useCountry();
  const { uid } = user;
  const logger = Logger('Bank Accounts');
  const storage = getStorage();
  const { accounts, maxAccounts, oAuthState } = props;
  const { updateAccounts, classes, multiple, isCurrencyEnabled } = props;
  const [show, setShow] = useState(false);
  const [currency, setCurrency] = useState(isCurrencyEnabled ? 'CAN' : 'US');
  const [showPlaid, setShowPlaid] = useState(false);
  const [plaidToken, setPlaidToken] = useState(null);
  const [plaidOAuthToken, setPlaidOAuthToken] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [limit, setLimit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editName, setEditName] = useState(null);
  const [bankName, setBankName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState({
    banks: false,
    accounts: false,
    updateBank: false,
  });
  const [banks, loadingBanks, refetchBanks] = useBanks();
  const getBankAccounts = useGetAccounts();
  const addPlaidError = useAddPlaidError();
  const addBank = useAddBank();
  const updateBank = useUpdateBank();
  const removeBank = useRemoveBank();
  const expireBank = useExpireBank();
  const getPlaidToken = useQuery('getPlaidToken', {
    fetchPolicy: 'network-only',
    request: country => ({
      country,
      userId: uid,
      webhook: `${getAPIServer()}/plaidhook`,
    }),
    response: res => res.data.getPlaidToken,
  });
  const onSuccess = useCallback(
    (publicToken, metadata) => {
      setShowPlaid(false);
      storage.removeItem('plaidToken');
      setTimeout(() => {
        setSelected(null);
        setLoading({ ...loading, banks: true });
      }, 100);
      addBank(publicToken, metadata, country === 'CAN' ? currency : 'US')
        .then(response => {
          if (response.status === 'pending_manual') {
            setEditName(response);
            setShow(true);
          }
          return refetchBanks();
        })
        .then(() => {
          setLoading({ ...loading, banks: false });
          setCurrency(isCurrencyEnabled ? 'CAN' : 'US');
        })
        .catch(err => logger.error(err));
    },
    [loading, country, currency],
  );
  const onExit = useCallback(
    (error, metadata) => {
      const { institution, status } = metadata;
      const bank = selected || institution;
      setShowPlaid(false);
      storage.removeItem('plaidToken');
      if (error === {} && !bank.name && status === 'requires_credentials') {
        setSelected(null);
      } else {
        if (error && error.error_code === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
          expireBank(selected.id).then(() => refetchBanks());
          setDeleting(true);
          setShow(true);
        }
        addPlaidError(error, metadata);
        setSelected(null);
      }
      setCurrency(isCurrencyEnabled ? 'CAN' : 'US');
    },
    [selected],
  );

  useEffect(() => {
    setPlaidToken(null);
    getPlaidToken(country === 'CAN' ? 'CA' : 'US')
      .then(token => {
        storage.setItem('plaidToken', token);
        setPlaidToken(token);
      })
      .catch(err => logger.error(err));
  }, [country]);

  useEffect(() => {
    const token = storage.getItem('plaidToken');
    setPlaidOAuthToken(token);
    if (token) {
      setTimeout(() => {
        window.document.querySelector('#PlaidOAuthButton').click();
      }, 500);
    }
  }, [oAuthState]);

  const remaining = maxAccounts - accounts.length;
  const limitReached =
    bankAccounts.reduce((m, i) => (m += i.checked), 0) >= remaining;

  const close = () => {
    setShow(false);
    setDeleting(false);
    setLoading({ ...loading, accounts: false });
    setBankName('');
    setEditName(null);
    setBankAccounts([]);
  };

  const handle = bank => {
    const bankName = parseBank(bank.name);
    setBankName(bankName);
    setLoading({ ...loading, accounts: true });
    setShow(true);
    getBankAccounts(bank.id)
      .then(response => {
        const { accounts, currency } = response;
        setBankAccounts(
          accounts.map(account => ({
            ...account,
            bankName,
            currencyType: currency,
            checked: false,
          })),
        );
        setLoading({ ...loading, accounts: false });
      })
      .catch(err => {
        logger.error(err);
        close();
      });
  };

  const checkAll = checked => {
    setBankAccounts(bankAccounts.map(a => ({ ...a, checked })));
  };

  const check = index => {
    bankAccounts[index].checked = !bankAccounts[index].checked;
    setBankAccounts([...bankAccounts]);
  };

  const select = index => {
    setBankAccounts(
      bankAccounts.map((a, i) => ({ ...a, checked: i === index })),
    );
  };

  const save = () => {
    if (editName) {
      const { id, name } = editName;
      setLoading({ ...loading, updateBank: true });
      updateBank(id, name)
        .then(() => refetchBanks())
        .then(() => {
          close();
          setLoading({ ...loading, updateBank: false });
        });
    } else {
      updateAccounts(bankAccounts.filter(account => account.checked));
      close();
    }
  };

  const handleRemove = id => {
    removeBank(id).then(() => {
      setRemoving(null);
      refetchBanks();
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="h5" noMargin>
          Connected banks
        </Typography>
        <Typography variant="body1" short className={classes.description}>
          Connect up to 3 banks.
        </Typography>
        <LimitModal show={limit} onClose={() => setLimit(false)} />
        <DeleteModal
          show={!!removing}
          type="bank"
          onClose={() => setRemoving(null)}
          onDelete={() => handleRemove(removing)}
        />
      </Grid>
      <Grid item xs={12} md={6} className={classes.addButton}>
        <Button
          short
          secondary
          startIcon={<AddIcon />}
          onClick={() => {
            if (banks.length >= 3) {
              setLimit(true);
            } else if (country === 'US') {
              setTimeout(() => {
                window.document.querySelector('#PlaidButton').click();
              }, 500);
            } else {
              setShowPlaid(true);
            }
          }}
        >
          Connect to a bank
        </Button>
        {plaidToken && (
          <PlaidButton
            token={plaidToken}
            banks={banks}
            setLimit={setLimit}
            onExit={onExit}
            onSuccess={onSuccess}
          />
        )}
        {plaidOAuthToken && (
          <PlaidOAuthButton
            token={plaidOAuthToken}
            onExit={onExit}
            onSuccess={onSuccess}
          />
        )}
        <Modal open={showPlaid} maxWidth={450}>
          <Modal.Title warning>
            <WarningIcon /> Bank currency
          </Modal.Title>
          <Modal.Content>
            <Typography variant="body2">
              Ensure your bank accepts the currency paid by the production.{' '}
              <br />
              <br />
              If your account is not compatible with the currency being paid,{' '}
              <Typography
                medium
                variant="body2"
                component="span"
                className={classes.remove}
              >
                this will result in errors with direct deposit setup.
              </Typography>
              <br />
              <br />
            </Typography>
          </Modal.Content>
          <Modal.Actions>
            <Grid container spacing={2} justify="flex-end">
              <Grid item>
                <Button secondary onClick={() => setShowPlaid(false)}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  disabled={!plaidToken}
                  onClick={() => {
                    setShowPlaid(false);
                    setTimeout(() => {
                      window.document.querySelector('#PlaidButton').click();
                    }, 500);
                  }}
                >
                  I understand
                </Button>
              </Grid>
            </Grid>
          </Modal.Actions>
        </Modal>
        <Modal
          onClose={close}
          open={show}
          disableBackdropClick
          disableEscapeKeyDown
        >
          <Modal.Title onClose={close}>
            {deleting
              ? 'Unable to Verify Bank'
              : editName
              ? 'Bank Name'
              : `Add Account From ${bankName}`}
          </Modal.Title>
          <Modal.Content style={{ minWidth: editName ? 400 : 500 }}>
            {loading.accounts || loading.updateBank ? (
              <Loader />
            ) : deleting ? (
              <Typography variant="body2" size="long">
                One or both of the amounts you specified did not match the
                microdeposits made to your account. Please try connect to this
                bank again.
                <br />
                <br />
                If you're still having trouble after retrying and need
                assistance, please call Employee Help Desk at{' '}
                <Link href="tel:+18885704650">888.570.4650</Link>.
              </Typography>
            ) : editName ? (
              <Grid container spacing={2}>
                <Grid item xs={12}></Grid>
                <Grid item xs={12} style={{ marginBottom: 8 }}>
                  <TextField
                    fullWidth
                    label={
                      <>
                        What is the name of this bank? <em>(max 30 chars.)</em>
                      </>
                    }
                    value={editName.name}
                    inputProps={{ maxLength: 30 }}
                    onChange={event =>
                      setEditName({ ...editName, name: event.target.value })
                    }
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {multiple && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      You can select up to {remaining} more account
                      {remaining > 1 ? '(s)' : ''}.
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Table dark size="short" style={{ marginBottom: 8 }}>
                    <Table.Head>
                      <Table.Row>
                        <Table.Cell>
                          {multiple && (
                            <Checkbox
                              size="short"
                              disabled={
                                limitReached || remaining < bankAccounts.length
                              }
                              indeterminate={
                                !bankAccounts.every(a => a.checked) &&
                                bankAccounts.some(a => a.checked)
                              }
                              checked={bankAccounts.every(a => a.checked)}
                              onChange={(_, checked) => checkAll(checked)}
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell>Account Type</Table.Cell>
                        <Table.Cell>Account Number</Table.Cell>
                      </Table.Row>
                    </Table.Head>
                    <Table.Body>
                      {bankAccounts.map((item, index) => (
                        <Table.Row
                          key={item.account}
                          className={classes.clickable}
                          onClick={() => {
                            if (multiple) {
                              if (limitReached && !item.checked) {
                                return;
                              }
                              check(index);
                            } else {
                              select(index);
                            }
                          }}
                        >
                          <Table.Cell>
                            {multiple ? (
                              <Checkbox
                                size="short"
                                disabled={limitReached && !item.checked}
                                checked={item.checked}
                              />
                            ) : (
                              <Radio size="short" checked={item.checked} />
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {format.capitalize(item.subtype)}
                          </Table.Cell>
                          <Table.Cell>{item.account}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Grid>
              </Grid>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Grid container spacing={2} justify="flex-end">
              {deleting ? (
                <Grid item>
                  <Button onClick={() => close()}>Ok</Button>
                </Grid>
              ) : (
                <>
                  <Grid item>
                    <Button
                      secondary
                      disabled={!!editName}
                      onClick={() => close()}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={save}
                      disabled={
                        (!editName && !bankAccounts.some(a => a.checked)) ||
                        (editName && editName.name.length === 0)
                      }
                    >
                      {editName ? 'Save' : 'Add'}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Modal.Actions>
        </Modal>
      </Grid>
      <Grid item xs={12} style={{ overflowX: 'auto' }}>
        <Table hover>
          <Table.Head>
            <Table.Row>
              <Table.Cell></Table.Cell>
              <Table.Cell>Bank Name</Table.Cell>
              <Table.Cell>Status</Table.Cell>
              {isCurrencyEnabled && <Table.Cell>Currency</Table.Cell>}
              <Table.Cell>Action</Table.Cell>
              <Table.Cell></Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            <>
              {banks.length > 0 &&
                banks.map(bank => (
                  <Table.Row key={bank.id}>
                    <Table.Cell align="center" className={classes.bankIconCell}>
                      {bank.logo ? (
                        <img
                          className={classes.bankIcon}
                          src={`data:image/png;base64,${bank.logo}`}
                          alt={`${bank.name} Logo`}
                        />
                      ) : (
                        <BankIcon />
                      )}
                    </Table.Cell>
                    <Table.Cell>{parseBank(bank.name)}</Table.Cell>
                    <Table.Cell>
                      <BankStatus bank={bank} />
                    </Table.Cell>
                    {isCurrencyEnabled && (
                      <Table.Cell>
                        {bank.currency === 'CAN' ? 'CAD' : 'USD'}
                      </Table.Cell>
                    )}
                    <Table.Cell>
                      <BankAction
                        bank={bank}
                        limited={limitReached}
                        onAccounts={handle}
                        setSelected={setSelected}
                        onSuccess={onSuccess}
                        onExit={onExit}
                      />
                    </Table.Cell>
                    <Table.Cell padding="checkbox" align="right">
                      <IconButton onClick={() => setRemoving(bank.id)}>
                        <DeleteIcon
                          fontSize="small"
                          className={classes.remove}
                        />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                ))}
              {(loadingBanks || loading.banks || banks.length === 0) && (
                <Table.Row className={classes.empty}>
                  <Table.Cell colSpan={5} align="center">
                    {loadingBanks || loading.banks ? (
                      <Loader size={24} />
                    ) : (
                      'No bank connected yet'
                    )}
                  </Table.Cell>
                </Table.Row>
              )}
            </>
          </Table.Body>
        </Table>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(BankAccounts);
